import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { PHONE, PHONE_HREF, SERVICE_LIST } from "@/data/services";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setToolsOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-bg/95 backdrop-blur-md border-b border-line" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-page px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <img
            src="/logo-icon.png"
            alt="Styne Group"
            className="h-11 md:h-12 w-auto"
            data-testid="header-logo"
          />
          <span className="flex flex-col leading-none">
            <span className="font-display text-xl md:text-2xl font-bold tracking-wider text-ink">STYNE</span>
            <span className="font-body text-[10px] tracking-[0.4em] text-muted mt-1">GROUP</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              data-testid="nav-services-button"
              className="nav-link flex items-center gap-1.5"
            >
              Services <ChevronDown size={14} />
            </button>
            {servicesOpen && (
              <div
                data-testid="services-dropdown"
                className="absolute left-0 top-full pt-3 w-72"
              >
                <div className="bg-elevated border border-line py-2">
                  {SERVICE_LIST.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`nav-service-${s.slug}`}
                      className="block px-5 py-3 font-display text-sm tracking-widerx uppercase text-ink hover:text-gold hover:bg-card transition-colors"
                    >
                      {s.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <NavLink to="/services/emergency-plumbing" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} data-testid="nav-emergency">
            Emergency
          </NavLink>
          <div
            className="relative"
            onMouseEnter={() => setToolsOpen(true)}
            onMouseLeave={() => setToolsOpen(false)}
          >
            <button data-testid="nav-tools-button" className="nav-link flex items-center gap-1.5">
              <span className="inline-flex items-center gap-1.5">
                Tools
                <span className="text-[8px] tracking-widest bg-gold/15 text-gold px-1.5 py-0.5 border border-gold/30">FREE</span>
              </span>
              <ChevronDown size={14} />
            </button>
            {toolsOpen && (
              <div className="absolute left-0 top-full pt-3 w-64" data-testid="tools-dropdown">
                <div className="bg-elevated border border-line py-2">
                  <Link to="/tools" data-testid="nav-tools-all" className="block px-5 py-3 font-display text-sm tracking-widerx uppercase text-ink hover:text-gold hover:bg-card transition-colors">All Free Tools</Link>
                  <Link to="/tools/leak-test" data-testid="nav-tools-leak" className="block px-5 py-3 font-display text-sm tracking-widerx uppercase text-ink hover:text-gold hover:bg-card transition-colors">Leak Test</Link>
                  <Link to="/tools/rebate-finder" data-testid="nav-tools-rebate" className="block px-5 py-3 font-display text-sm tracking-widerx uppercase text-ink hover:text-gold hover:bg-card transition-colors">Rebate Finder</Link>
                  <Link to="/tools/pressure-test" data-testid="nav-tools-pressure" className="block px-5 py-3 font-display text-sm tracking-widerx uppercase text-ink hover:text-gold hover:bg-card transition-colors">Pressure Test</Link>
                  <Link to="/tools/hot-water-decision" data-testid="nav-tools-hwdecision" className="block px-5 py-3 font-display text-sm tracking-widerx uppercase text-ink hover:text-gold hover:bg-card transition-colors">Repair or Replace</Link>
                  <Link to="/pricing" data-testid="nav-tools-pricing" className="block px-5 py-3 font-display text-sm tracking-widerx uppercase text-ink hover:text-gold hover:bg-card transition-colors">Pricing Guide</Link>
                </div>
              </div>
            )}
          </div>
          <NavLink to="/gallery" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} data-testid="nav-gallery">
            Gallery
          </NavLink>
          <NavLink to="/areas" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} data-testid="nav-areas">
            Areas
          </NavLink>
          <NavLink to="/about" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} data-testid="nav-about">
            About
          </NavLink>
          <NavLink to="/contact" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} data-testid="nav-contact">
            Contact
          </NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-3 xl:gap-5">
          <div className="hidden xl:flex items-center gap-2 px-3 py-1.5 border border-gold/40 bg-gold/5" data-testid="header-open-247">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-gold"></span>
            </span>
            <span className="font-display text-xs tracking-widerx text-gold">Open 24/7</span>
          </div>
          <a
            href={PHONE_HREF}
            data-testid="header-phone"
            className="hidden xl:flex items-center gap-2 text-ink hover:text-gold transition-colors"
          >
            <Phone size={16} className="text-gold" />
            <span className="font-display text-sm tracking-widerx">{PHONE}</span>
          </a>
          <Link to="/contact" data-testid="header-cta" className="btn-gold">
            Get a Quote
          </Link>
        </div>

        {/* Mobile button */}
        <button
          data-testid="mobile-menu-button"
          className="lg:hidden text-ink p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-elevated border-t border-line">
          <div className="px-6 py-6 flex flex-col gap-1">
            <div className="flex items-center gap-2 px-3 py-1.5 border border-gold/40 bg-gold/5 self-start mb-5" data-testid="mobile-open-247">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-gold"></span>
              </span>
              <span className="font-display text-xs tracking-widerx text-gold">Open 24/7</span>
            </div>
            <div className="text-muted text-[10px] tracking-[0.3em] uppercase mb-2">Services</div>            {SERVICE_LIST.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`nav-service-${s.slug}`}
                onClick={() => setOpen(false)}
                className="font-display text-lg tracking-wide uppercase text-ink py-2 hover:text-gold"
              >
                {s.title}
              </Link>
            ))}
            <div className="h-px bg-line my-4" />
            <Link to="/tools/leak-test" data-testid="mobile-nav-leak" onClick={() => setOpen(false)} className="font-display text-lg uppercase text-ink py-2 hover:text-gold">Leak Test</Link>
            <Link to="/tools/rebate-finder" data-testid="mobile-nav-rebate" onClick={() => setOpen(false)} className="font-display text-lg uppercase text-ink py-2 hover:text-gold">Rebate Finder</Link>
            <Link to="/tools/pressure-test" data-testid="mobile-nav-pressure" onClick={() => setOpen(false)} className="font-display text-lg uppercase text-ink py-2 hover:text-gold">Pressure Test</Link>
            <Link to="/tools/hot-water-decision" data-testid="mobile-nav-hwdecision" onClick={() => setOpen(false)} className="font-display text-lg uppercase text-ink py-2 hover:text-gold">Repair or Replace</Link>
            <Link to="/pricing" data-testid="mobile-nav-pricing" onClick={() => setOpen(false)} className="font-display text-lg uppercase text-ink py-2 hover:text-gold">Pricing Guide</Link>
            <Link to="/gallery" data-testid="mobile-nav-gallery" onClick={() => setOpen(false)} className="font-display text-lg uppercase text-ink py-2 hover:text-gold">Gallery</Link>
            <Link to="/areas" data-testid="mobile-nav-areas" onClick={() => setOpen(false)} className="font-display text-lg uppercase text-ink py-2 hover:text-gold">Areas</Link>
            <Link to="/about" data-testid="mobile-nav-about" onClick={() => setOpen(false)} className="font-display text-lg uppercase text-ink py-2 hover:text-gold">About</Link>
            <Link to="/contact" data-testid="mobile-nav-contact" onClick={() => setOpen(false)} className="font-display text-lg uppercase text-ink py-2 hover:text-gold">Contact</Link>
            <div className="h-px bg-line my-4" />
            <a href={PHONE_HREF} className="flex items-center gap-3 text-ink py-2">
              <Phone size={18} className="text-gold" />
              <span className="font-display text-lg tracking-widerx">{PHONE}</span>
            </a>
            <Link to="/contact" className="btn-gold mt-3 self-start">Get a Quote</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
